<script setup lang="ts">
import { EmptyState, TraceIdAlert, formatDateTime } from '@yc/ui-kit'
import { computed, onMounted, reactive, ref } from 'vue'

import {
  STATUS_LABELS,
  createWarehouse,
  listWarehouses,
  updateWarehouse,
  type Warehouse,
  type WarehouseInput,
} from '../api/warehouses'

const rows = ref<Warehouse[]>([])
const total = ref(0)
const loading = ref(false)
const error = ref<unknown>(null)
const notice = ref('')

const query = reactive({ keyword: '', status: '', page: 1, size: 20 })

const formOpen = ref(false)
const editing = ref<Warehouse | null>(null)
const saving = ref(false)
const formError = ref<unknown>(null)
const form = reactive<WarehouseInput>({ code: '', name: '', address: '', status: 'active' })

const codeError = computed(() => (form.code.trim() ? '' : '请输入仓库编码'))

async function load(noticeText = ''): Promise<void> {
  loading.value = true
  error.value = null
  try {
    const res = await listWarehouses({
      page: query.page,
      size: query.size,
      keyword: query.keyword.trim() || undefined,
      status: query.status || undefined,
    })
    rows.value = res.list
    total.value = res.total
    notice.value = noticeText
  } catch (e) {
    error.value = e
    rows.value = []
  } finally {
    loading.value = false
  }
}

onMounted(() => void load())

function search(): void {
  query.page = 1
  void load()
}

function page(p: number): void {
  query.page = p
  void load()
}

function openCreate(): void {
  editing.value = null
  Object.assign(form, { code: '', name: '', address: '', status: 'active' })
  formError.value = null
  formOpen.value = true
}

function openEdit(row: Warehouse): void {
  editing.value = row
  Object.assign(form, { code: row.code, name: row.name, address: row.address, status: row.status })
  formError.value = null
  formOpen.value = true
}

async function save(): Promise<void> {
  if (codeError.value) return
  saving.value = true
  formError.value = null
  const input: WarehouseInput = {
    code: form.code.trim(),
    name: form.name.trim(),
    address: form.address.trim(),
    status: form.status,
  }
  try {
    if (editing.value) await updateWarehouse(editing.value.id, input)
    else await createWarehouse(input)
    formOpen.value = false
    await load(editing.value ? '仓库已更新' : '仓库已创建')
  } catch (e) {
    formError.value = e
  } finally {
    saving.value = false
  }
}
</script>

<template>
  <div class="yc-wh">
    <header class="yc-wh__bar">
      <el-input
        v-model="query.keyword"
        class="yc-wh__keyword"
        placeholder="仓库编码 / 名称"
        clearable
        @keyup.enter="search"
        @clear="search"
      />
      <el-select v-model="query.status" class="yc-wh__status" placeholder="全部状态" clearable @change="search">
        <el-option v-for="(label, value) in STATUS_LABELS" :key="value" :label="label" :value="value" />
      </el-select>
      <el-button @click="search">查询</el-button>
      <el-button v-perm="'wms:warehouse:create'" type="primary" @click="openCreate">新建仓库</el-button>
      <el-button @click="load()">刷新</el-button>
      <span v-if="notice" class="yc-wh__notice">{{ notice }}</span>
    </header>

    <div v-if="error" class="yc-wh__error">
      <TraceIdAlert :error="error" />
    </div>

    <el-card v-loading="loading" class="yc-wh__card">
      <el-table v-if="rows.length" :data="rows" row-key="id">
        <el-table-column prop="code" label="仓库编码" min-width="140" />
        <el-table-column prop="name" label="名称" min-width="160" show-overflow-tooltip />
        <el-table-column prop="address" label="地址" min-width="220" show-overflow-tooltip />
        <el-table-column label="状态" width="90">
          <template #default="{ row }">
            <el-tag :type="row.status === 'active' ? 'success' : 'info'" size="small">
              {{ STATUS_LABELS[row.status as Warehouse['status']] ?? row.status }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="更新时间" min-width="160">
          <template #default="{ row }">{{ formatDateTime(row.updated_at) }}</template>
        </el-table-column>
        <el-table-column label="操作" width="100" fixed="right">
          <template #default="{ row }">
            <el-button v-perm="'wms:warehouse:update'" link type="primary" @click="openEdit(row)">编辑</el-button>
          </template>
        </el-table-column>
      </el-table>

      <EmptyState
        v-else-if="!loading"
        title="还没有仓库"
        description="首次进入会自动创建一个「默认仓」，也可以按业务需要新建仓库。"
      >
        <el-button v-perm="'wms:warehouse:create'" type="primary" @click="openCreate">新建仓库</el-button>
      </EmptyState>
    </el-card>

    <el-pagination
      v-if="total > query.size"
      class="yc-wh__page"
      layout="total, prev, pager, next"
      :total="total"
      :page-size="query.size"
      :current-page="query.page"
      @current-change="page"
    />

    <el-dialog
      v-if="formOpen"
      v-model="formOpen"
      :title="editing ? `编辑仓库 · ${editing.code}` : '新建仓库'"
      width="480px"
      :close-on-click-modal="false"
    >
      <div v-if="formError" class="yc-wh__error">
        <TraceIdAlert :error="formError" />
      </div>

      <el-form label-position="top" @submit.prevent="save">
        <el-form-item label="仓库编码" :error="codeError">
          <el-input v-model="form.code" maxlength="32" placeholder="如 WH1" />
          <span class="yc-wh__hint">同一货主内唯一；默认仓的编码是 DEFAULT。</span>
        </el-form-item>

        <el-form-item label="名称">
          <el-input v-model="form.name" maxlength="64" placeholder="如 上海一号仓" />
        </el-form-item>

        <el-form-item label="地址">
          <el-input v-model="form.address" maxlength="255" placeholder="如 上海市青浦区华新镇 xx 路 1 号" />
        </el-form-item>

        <el-form-item label="状态">
          <el-radio-group v-model="form.status">
            <el-radio value="active">启用</el-radio>
            <el-radio value="disabled">停用</el-radio>
          </el-radio-group>
        </el-form-item>

        <el-button type="primary" native-type="submit" :loading="saving" :disabled="!!codeError">
          {{ editing ? '保存' : '创建' }}
        </el-button>
      </el-form>

      <template #footer>
        <el-button @click="formOpen = false">取消</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<style scoped lang="scss">
.yc-wh {
  &__bar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--yc-space-2);
    margin-bottom: var(--yc-space-4);
  }

  &__keyword {
    width: 200px;
  }

  &__status {
    width: 140px;
  }

  &__notice {
    color: var(--yc-text-secondary);
    font-size: var(--yc-font-sm);
  }

  &__error {
    margin-bottom: var(--yc-space-3);
    font-size: var(--yc-font-sm);
  }

  &__page {
    margin-top: var(--yc-space-4);
    justify-content: flex-end;
  }

  &__hint {
    display: block;
    margin-top: var(--yc-space-1);
    color: var(--yc-text-secondary);
    font-size: var(--yc-font-xs);
    line-height: 1.6;
  }
}
</style>
